import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { CartService } from '../../../shared/services/cart.service';
import { CartSidebarService } from '../../../shared/services/cart-sidebar.service';
import { ShopService } from '../../../shared/services/shop.service';
import { ShopProduct } from '../../../shared/interfaces/shop-product.interface';
import { Subscription, BehaviorSubject } from 'rxjs';
import { RouterLink } from '@angular/router';
import { Router } from '@angular/router';
import { CheckoutService } from '../../../shared/services/checkout.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-cart-sidebar',
  standalone: true,
  imports: [CommonModule, MatIconModule, RouterLink],
  templateUrl: './cart-sidebar.component.html',
  styleUrls: ['./cart-sidebar.component.scss']
})
export class CartSidebarComponent implements OnInit, OnDestroy {
  isOpen = false;
  private subscriptions: Subscription[] = [];
  private productsMap = new Map<string, ShopProduct>();
  private productsMapSubject = new BehaviorSubject<Map<string, ShopProduct>>(new Map());
  products$ = this.productsMapSubject.asObservable();
  cartProducts: ShopProduct[] = [];
  private subscription: Subscription = new Subscription();
  selectedCountry: string = 'AT';

  // Estimated VAT rates for display only
  private readonly VAT_RATES: Record<string, number> = {
    AT: 0.20, // Austria 20%
    DE: 0.19, // Germany 19%
    CH: 0.077, // Switzerland 7.7%
    GB: 0.20, // UK 20%
    US: 0, // USA no VAT
    CA: 0.05, // Canada 5% GST
    default: 0.20 // Default to Austrian VAT
  };

  constructor(
    public cartService: CartService,
    public cartSidebarService: CartSidebarService,
    private shopService: ShopService,
    private router: Router,
    private checkoutService: CheckoutService
  ) {
    this.subscription.add(
      this.checkoutService.selectedCountry$.subscribe(country => {
        this.selectedCountry = country;
      })
    );
  }

  ngOnInit() {
    this.subscriptions.push(
      this.cartSidebarService.isOpen$.subscribe(
        isOpen => this.isOpen = isOpen
      )
    );

    this.subscriptions.push(
      this.shopService.getAllProducts().subscribe(products => {
        this.productsMap.clear();
        products.forEach(product => {
          this.productsMap.set(product.id, product);
        });
        this.productsMapSubject.next(this.productsMap);
      })
    );

    this.loadCartProducts();
  }

  private async loadCartProducts() {
    const cartItems = Array.from(this.cartService.getCartItems());
    const products = await firstValueFrom(this.shopService.getAllProducts());
    
    if (products) {
      this.cartProducts = products.filter(product => 
        cartItems.includes(product.id)
      );
    }
  }

  getTotal(): number {
    return this.cartProducts.reduce((sum, product) => sum + product.price, 0);
  }

  getBasePrice(): number {
    return this.cartProducts.reduce((sum, product) => sum + product.price, 0);
  }

  getEstimatedTaxRate(): number {
    return this.VAT_RATES[this.selectedCountry] || this.VAT_RATES['default'];
  }

  getEstimatedTaxAmount(): number {
    return this.getBasePrice() * this.getEstimatedTaxRate();
  }

  getEstimatedTotal(): number {
    return this.getBasePrice() * (1 + this.getEstimatedTaxRate());
  }

  getProduct(productId: string): ShopProduct | undefined {
    return this.productsMap.get(productId);
  }

  getMonthlyPrice(productId: string): number {
    const product = this.getProduct(productId);
    return (product?.price || 0) / 18;
  }

  getMonthlyTotal(): number {
    return Array.from(this.cartService.getCartItems()).reduce((sum, productId) => {
      return sum + this.getMonthlyPrice(productId);
    }, 0);
  }

  getEstimatedMonthlyWithTax(): number {
    return this.getMonthlyTotal() * (1 + this.getEstimatedTaxRate());
  }

  calculateProductTotal(product: ShopProduct): number {
    return product.price;
  }

  calculateBaseTotal(): number {
    return this.cartProducts.reduce((total, item) => total + item.price, 0);
  }

  calculateTotal(): number {
    return this.calculateBaseTotal();
  }

  getTaxLabel(): string {
    switch(this.selectedCountry) {
      case 'CH':
        return 'MwSt';
      case 'DE':
      case 'AT':
        return 'USt';
      default:
        return 'VAT';
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
    this.subscription.unsubscribe();
  }

  removeFromCart(productId: string) {
    this.cartService.removeFromCart(productId);
  }
} 